import Autoplay from 'embla-carousel-autoplay';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import {
  buildDprSrcSet,
  getCloudflareDomain,
} from '~/components/CloudflareResponsiveImage/utils';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { userStore } from '~/stores';

import { AUTOPLAY_DELAY, getHeroBannerImageParams } from './constants';
import styles from './HeroBanner.module.scss';
import HeroBannerFrame from './HeroBannerFrame';
import type { HeroBannerProps } from './interfaces';

/**
 * Hero banner slider component for the main page.
 * Displays promotional banners with autoplay and pagination.
 */
const HeroBanner = ({ items, className }: HeroBannerProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [animationKey, setAnimationKey] = useState(0);

  const domain = getCloudflareDomain();

  /**
   * Per-viewport image sizing. The asset is chosen by the browser via
   * `<picture>`/`media` (not JS) so SSR already serves the viewport-correct
   * banner — no asset flip between server (UA) and client (viewport), and the
   * SSR `<link rel="preload">` tags line up exactly. Tablet upscales the base
   * width ×2 to stay crisp on the wider ≤1024px portrait frame.
   */
  const heroImageParams = useMemo(() => getHeroBannerImageParams(), []);
  const mobileParams = heroImageParams.mobile;
  const tabletParams = useMemo(
    () =>
      mobileParams.width && mobileParams.height
        ? {
            ...mobileParams,
            width: mobileParams.width * 2,
            height: mobileParams.height * 2,
          }
        : mobileParams,
    [mobileParams],
  );
  const desktopParams = heroImageParams.desktop;

  const plugins = useMemo(
    () => [Autoplay({ delay: AUTOPLAY_DELAY, stopOnInteraction: false })],
    [],
  );

  const onSelect = useCallback(() => {
    if (!api) return;
    setSelectedIndex(api.selectedScrollSnap());
  }, [api]);

  const resetAnimation = useCallback(() => {
    setAnimationKey((prev) => prev + 1);
  }, []);

  const scrollTo = useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api],
  );

  useEffect(() => {
    if (!api) return;

    onSelect();
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    api.on('pointerDown', resetAnimation);
    api.on('pointerUp', resetAnimation);

    /**
     * Workaround: register a non-passive touchmove on document so that
     * Chrome's compositor marks touchmove events as cancelable.
     * Without this, MCosmetic's stacking context (z-index on .content)
     * causes the compositor to miss Embla's non-passive listener,
     * resulting in cancelable:false which breaks drag.
     */
    const noop = (() => undefined) as EventListener;
    document.addEventListener('touchmove', noop, { passive: false });

    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
      api.off('pointerDown', resetAnimation);
      api.off('pointerUp', resetAnimation);
      document.removeEventListener('touchmove', noop);
    };
  }, [api, onSelect, resetAnimation]);

  const showPagination = items && items.length > 1;

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <HeroBannerFrame className={className}>
      <Carousel
        key={userStore.dir}
        opts={{
          loop: items.length > 1,
          watchDrag: items.length > 1,
          direction: userStore.dir,
        }}
        plugins={plugins}
        setApi={setApi}
        className={styles.carouselRoot}
      >
        <CarouselContent className={styles.viewport}>
          {items.map((item, index) => {
            const mobileSrc = item.previewImageThumb || item.previewImage;
            const desktopSrc = item.previewImage;

            return (
              <CarouselItem key={item.id} className={styles.slide}>
                <Link
                  to={`${item.deepLink}?showProducts=true`}
                  className={styles.link}
                >
                  <picture>
                    <source
                      media="(max-width: 767px)"
                      srcSet={buildDprSrcSet(mobileSrc, domain, mobileParams)}
                    />
                    <source
                      media="(min-width: 768px) and (max-width: 1024px)"
                      srcSet={buildDprSrcSet(mobileSrc, domain, tabletParams)}
                    />
                    <CloudflareResponseImage
                      alt={item.name}
                      className={styles.image}
                      loading={index === 0 ? 'eager' : 'lazy'}
                      fetchPriority={index === 0 ? 'high' : 'low'}
                      width={desktopParams.width}
                      height={desktopParams.height}
                      src={desktopSrc}
                      srcParams={desktopParams}
                    />
                  </picture>
                </Link>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
      {showPagination && (
        <div className={styles.pagination}>
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className={classes([
                styles.dot,
                index === selectedIndex && styles.dotActive,
              ])}
              onClick={() => scrollTo(index)}
            >
              {index === selectedIndex && (
                <span
                  key={`${selectedIndex}-${animationKey}`}
                  className={styles.dotProgress}
                  style={
                    {
                      '--autoplay-delay': `${AUTOPLAY_DELAY}ms`,
                    } as React.CSSProperties
                  }
                />
              )}
            </button>
          ))}
        </div>
      )}
    </HeroBannerFrame>
  );
};

export default observer(HeroBanner);
